'use client';

import { useEffect, useState } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { logger } from '@/lib/logger';
import Link from 'next/link';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { createClient } from '@/lib/supabase/client';

export default function PaymentSuccessPage() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Confirming your payment...');
  const supabase = createClient();

  useEffect(() => {
    const paymentIntent = searchParams.get('payment_intent');
    const paymentIntentClientSecret = searchParams.get('payment_intent_client_secret');

    if (!paymentIntent || !paymentIntentClientSecret) {
      setStatus('error');
      setMessage('Invalid payment confirmation');
      return;
    }

    // Check payment status
    async function checkPayment() {
      try {
        // Wait a moment for webhook to process
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Verify payment intent status with Stripe via API
        const response = await fetch(`/api/payments/verify?payment_intent=${paymentIntent}`);

        if (!response.ok) {
          const error = await response.json();
          setStatus('error');
          setMessage(error.error || 'Failed to verify payment status');
          return;
        }

        const { status: stripeStatus } = await response.json();

        // Only show success if payment is actually succeeded
        if (stripeStatus === 'succeeded') {
          // Double-check our database has recorded it
          const { data: payment } = await supabase
            .from('payments')
            .select('status, match_id')
            .eq('stripe_payment_id', paymentIntent)
            .single();

          if (payment?.status === 'charged') {
            setStatus('success');
            setMessage('Your payment was successful and the worker has been notified!');
          } else {
            // Stripe succeeded but webhook hasn't processed yet
            setStatus('success');
            setMessage('Payment successful! Processing your hire...');
          }
        } else if (stripeStatus === 'processing') {
          setStatus('success');
          setMessage('Payment is being processed. You will be notified once complete.');
        } else if (stripeStatus === 'requires_payment_method') {
          setStatus('error');
          setMessage('Payment failed. Please try again with a different payment method.');
        } else if (stripeStatus === 'canceled') {
          setStatus('error');
          setMessage('Payment was canceled. Please try again.');
        } else {
          setStatus('error');
          setMessage(`Payment status: ${stripeStatus}. Please contact support if you were charged.`);
        }
      } catch (error) {
        logger.error('Error checking payment:', error);
        setStatus('error');
        setMessage('Unable to verify payment status. Please contact support if you were charged.');
      }
    }

    checkPayment();
  }, [searchParams, supabase]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <Card className="w-full max-w-md">
        <CardContent className="pt-8 pb-6 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary" />
              <h1 className="mt-6 text-2xl font-bold">Confirming Payment</h1>
              <p className="mt-2 text-muted-foreground">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-500/10">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="mt-6 text-2xl font-bold">Payment Successful!</h1>
              <p className="mt-2 text-muted-foreground">{message}</p>
              <div className="mt-8 space-y-3">
                <Button asChild className="w-full h-12 text-base">
                  <Link href="/contractor/matches">Back to Matches</Link>
                </Button>
                <Button asChild variant="outline" className="w-full h-12 text-base">
                  <Link href="/dashboard">Go to Dashboard</Link>
                </Button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
                <AlertCircle className="h-10 w-10 text-destructive" />
              </div>
              <h1 className="mt-6 text-2xl font-bold">Payment Error</h1>
              <p className="mt-2 text-muted-foreground">{message}</p>
              <div className="mt-8 space-y-3">
                <Button asChild variant="outline" className="w-full h-12 text-base">
                  <Link href="/contractor/matches">Back to Matches</Link>
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
